import React, { useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Camera, Plus, Search, Utensils, Clock, Zap, Star } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { SmartFoodService, SmartFoodSuggestion } from '@/services/nutrition/SmartFoodService';

const MEAL_TYPES = [
  { id: 'breakfast', label: 'Breakfast', icon: '🌅', suggested: '7:00 AM' },
  { id: 'lunch', label: 'Lunch', icon: '☀️', suggested: '12:00 PM' },
  { id: 'snack', label: 'Snack', icon: '🍎', suggested: 'Anytime' },
  { id: 'dinner', label: 'Dinner', icon: '🌙', suggested: '7:00 PM' },
];

const QUICK_FOODS = [
  { name: 'Banana', calories: 105, icon: '🍌' },
  { name: 'Apple', calories: 95, icon: '🍎' },
  { name: 'Greek Yogurt', calories: 150, icon: '🥛' },
  { name: 'Almonds (1oz)', calories: 164, icon: '🥜' },
  { name: 'Protein Shake', calories: 200, icon: '🥤' },
  { name: 'Oatmeal', calories: 150, icon: '🥣' },
];

interface MobileMealLoggerProps {
  onMealAdded?: () => void;
  onLogMeal?: () => void;
}

export function MobileMealLogger({ onMealAdded, onLogMeal }: MobileMealLoggerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [smartSuggestions, setSmartSuggestions] = useState<SmartFoodSuggestion[]>([]);
  const isMobile = useIsMobile();

  const getCurrentMealType = () => {
    const hour = new Date().getHours();
    if (hour < 10) return 'breakfast';
    if (hour < 14) return 'lunch';
    if (hour < 17) return 'snack';
    return 'dinner';
  };

  const suggestedMealType = getCurrentMealType();

  // Auto-select suggested meal type when opening
  React.useEffect(() => {
    if (isOpen && !selectedMealType) {
      setSelectedMealType(suggestedMealType);
    }
  }, [isOpen, suggestedMealType, selectedMealType]);

  // Load smart suggestions when meal type changes
  React.useEffect(() => {
    if (selectedMealType) {
      const suggestions = SmartFoodService.getSmartSuggestions(selectedMealType);
      setSmartSuggestions(suggestions);
    }
  }, [selectedMealType]);

  const handleQuickAdd = (food: typeof QUICK_FOODS[0]) => {
    // Update food history for smart suggestions
    SmartFoodService.updateFoodHistory(food.name, selectedMealType, {
      calories: food.calories,
      quantity: 100,
    });
    
    console.log('Quick adding:', food);
    onMealAdded?.();
    setIsOpen(false);
  };

  const handleSmartSuggestionAdd = (suggestion: SmartFoodSuggestion) => {
    const food = suggestion.food;
    SmartFoodService.updateFoodHistory(food.name, selectedMealType, {
      calories: food.nutritionProfile.calories,
      protein: food.nutritionProfile.protein,
      carbs: food.nutritionProfile.carbs,
      fats: food.nutritionProfile.fats,
      quantity: food.averageQuantity,
    });
    
    console.log('Adding smart suggestion:', food);
    onMealAdded?.();
    setIsOpen(false);
  };

  const TriggerButton = (
    <Button size="lg" className="nutrition-button h-12 px-6 text-base font-medium">
      <Plus className="h-5 w-5 mr-2" />
      Log Meal
    </Button>
  );

  if (!isMobile) {
    // Desktop fallback - use existing modal
    return (
      <Button size="lg" onClick={onLogMeal} className="nutrition-button h-12 px-6 text-base font-medium">
        <Plus className="h-5 w-5 mr-2" />
        Log Meal
      </Button>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        {TriggerButton}
      </SheetTrigger>
      
      <SheetContent 
        side="bottom" 
        className="h-[85vh] rounded-t-lg p-0 border-t border-border/40"
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <SheetHeader className="p-6 pb-4 border-b">
            <SheetTitle className="text-xl font-semibold">Add to Your Meal</SheetTitle>
            <SheetDescription>
              What did you eat? Choose a meal type and add your food.
            </SheetDescription>
          </SheetHeader>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Meal Type Selection */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Meal Type</Label>
              <div className="grid grid-cols-2 gap-3">
                {MEAL_TYPES.map((meal) => (
                  <Card 
                    key={meal.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      selectedMealType === meal.id 
                        ? 'ring-2 ring-primary bg-primary/5' 
                        : suggestedMealType === meal.id 
                        ? 'ring-1 ring-primary/50 bg-primary/5' 
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => setSelectedMealType(meal.id)}
                  >
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl mb-2">{meal.icon}</div>
                      <div className="font-medium text-sm">{meal.label}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {meal.suggested}
                      </div>
                      {suggestedMealType === meal.id && !selectedMealType && (
                        <Badge variant="secondary" className="mt-2 text-xs">
                          Suggested
                        </Badge>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Search */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Search Food</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search for food or scan barcode..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 text-base"
                />
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-3">
              <Button variant="outline" size="lg" className="flex-1 h-12">
                <Camera className="h-4 w-4 mr-2" />
                Take Photo
              </Button>
              <Button variant="outline" size="lg" className="flex-1 h-12">
                <Search className="h-4 w-4 mr-2" />
                Scan Barcode
              </Button>
            </div>

            {/* Smart Suggestions - Based on user history */}
            {smartSuggestions.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-medium">Smart Suggestions</Label>
                  <Badge variant="secondary" className="text-xs">
                    <Zap className="h-3 w-3 mr-1" />
                    AI-Powered
                  </Badge>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {smartSuggestions.slice(0, 4).map((suggestion, index) => {
                    const food = suggestion.food;
                    const reasonLabels = {
                      frequent: 'Often eaten',
                      time_based: 'Usually now',
                      macro_goal: 'Fits goals',
                      recent_favorite: 'Recent favorite'
                    };
                    
                    return (
                      <Card 
                        key={index}
                        className="cursor-pointer hover:shadow-md transition-all hover:bg-muted/50 border-primary/20"
                        onClick={() => handleSmartSuggestionAdd(suggestion)}
                      >
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                              <Star className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="font-medium text-sm truncate">{food.name}</div>
                                <Badge variant="outline" className="text-xs shrink-0">
                                  {reasonLabels[suggestion.reason]}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <span>{Math.round(food.nutritionProfile.calories)} cal</span>
                                <span>{Math.round(food.averageQuantity)}g usual</span>
                                <span className="flex items-center gap-1">
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  {Math.round(suggestion.confidence * 100)}%
                                </span>
                              </div>
                            </div>
                          </div>
                          <Plus className="h-5 w-5 text-muted-foreground shrink-0 ml-2" />
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quick Add Foods */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Quick Add</Label>
              <div className="grid grid-cols-1 gap-3">
                {QUICK_FOODS.map((food, index) => (
                  <Card 
                    key={index}
                    className="cursor-pointer hover:shadow-md transition-all hover:bg-muted/50"
                    onClick={() => handleQuickAdd(food)}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{food.icon}</span>
                        <div>
                          <div className="font-medium">{food.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {food.calories} calories
                          </div>
                        </div>
                      </div>
                      <Plus className="h-5 w-5 text-muted-foreground" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Custom Entry */}
            <Card className="border-dashed">
              <CardContent className="p-4 text-center">
                <Utensils className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <div className="font-medium mb-1">Can't find your food?</div>
                <div className="text-sm text-muted-foreground mb-3">
                  Create a custom entry with your own nutritional information
                </div>
                <Button variant="outline" size="sm">
                  Create Custom Entry
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Footer */}
          <div className="p-6 pt-4 border-t bg-muted/30">
            <Button 
              size="lg" 
              className="w-full h-12 text-base font-medium"
              disabled={!selectedMealType}
              onClick={() => {
                onMealAdded?.();
                setIsOpen(false);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add to {selectedMealType ? MEAL_TYPES.find(m => m.id === selectedMealType)?.label : 'Meal'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}